import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Film, ChevronLeft, Loader2, Eye, TrendingUp, TrendingDown, Minus,
  AlertCircle
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const ZONE_CONFIG = {
  clogging: { label: 'Clogging', color: '#EF4444', bg: 'bg-red-500/20' },
  coasting: { label: 'Coasting', color: '#F59E0B', bg: 'bg-yellow-500/20' },
  elite: { label: 'Elite', color: '#22C55E', bg: 'bg-green-500/20' }
};

const GRADE_CONFIG = {
  'A': { color: '#22C55E', label: 'Elite Performance' },
  'B': { color: '#84CC16', label: 'Strong Progress' },
  'C': { color: '#F59E0B', label: 'Steady Improvement' },
  'D': { color: '#F97316', label: 'Needs Focus' },
  'F': { color: '#EF4444', label: 'Critical Attention' }
};

const FilmRoom = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    const token = localStorage.getItem('academy_token');
    if (!token) {
      toast.error('Please log in');
      navigate('/academy/enroll');
      return;
    }

    try {
      const response = await api.get('/academy/lms/scouting-reports/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setReports(response.data.reports || []);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Please complete enrollment');
        navigate('/academy/enroll');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white pt-16 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Graph Paper Background */}
      <div 
        className="fixed inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(249,115,22,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.5) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />
      
      <div className="container mx-auto px-6 py-8 relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/academy/lms')}
              variant="ghost"
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to LMS
            </Button>
          </div>
        </div>

        {/* Title Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center border border-orange-500/30">
              <Film className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                <span className="text-orange-500">FILM ROOM</span> • SCOUTING REPORTS
              </h1>
              <p className="text-gray-500 text-sm uppercase tracking-widest">Weekly Performance Analysis</p>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <Card className="bg-gray-900/80 border-gray-800 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Weekly Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            {reports.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-4 px-4 text-gray-400 font-bold text-sm uppercase tracking-wider">Week</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-bold text-sm uppercase tracking-wider">Status</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-bold text-sm uppercase tracking-wider">Math Score</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-bold text-sm uppercase tracking-wider">ELA Score</th>
                      <th className="text-left py-4 px-4 text-gray-400 font-bold text-sm uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report, idx) => {
                      const zoneConfig = ZONE_CONFIG[report.zone] || ZONE_CONFIG.coasting;
                      const gradeConfig = GRADE_CONFIG[report.status] || { color: '#888', label: 'N/A' };
                      
                      return (
                        <tr 
                          key={report.id || idx}
                          className="border-b border-gray-800/50 hover:bg-gray-800/50 transition-colors"
                        >
                          <td className="py-4 px-4">
                            <span className="text-white font-bold">Week {report.week}</span>
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <span 
                                className="w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                                style={{ 
                                  backgroundColor: `${gradeConfig.color}20`,
                                  color: gradeConfig.color
                                }}
                              >
                                {report.status || '-'}
                              </span>
                              <span 
                                className="px-2 py-1 rounded text-xs font-bold uppercase"
                                style={{ 
                                  backgroundColor: `${zoneConfig.color}20`,
                                  color: zoneConfig.color
                                }}
                              >
                                {zoneConfig.label}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-white font-mono text-lg">{report.math_score || '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-white font-mono text-lg">{report.ela_score || '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <Button
                              onClick={() => setSelectedReport(report)}
                              size="sm"
                              variant="outline"
                              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View Full Report
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <Film className="w-16 h-16 text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">No scouting reports yet</p>
                <p className="text-gray-600 text-sm">Your weekly reports will appear here as you progress through the program.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Full Report Modal */}
        {selectedReport && (
          <div 
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedReport(null)}
          >
            <div 
              className="bg-gray-900 border border-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Graph Paper Background */}
              <div 
                className="absolute inset-0 opacity-5 pointer-events-none rounded-lg"
                style={{
                  backgroundImage: 'linear-gradient(rgba(249,115,22,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.5) 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}
              />
              
              <div className="relative p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800">
                  <div>
                    <h2 className="text-xl font-black text-orange-500 uppercase tracking-wider">Scouting Report</h2>
                    <p className="text-gray-500 text-sm">Week {selectedReport.week}</p>
                  </div>
                  <button 
                    onClick={() => setSelectedReport(null)}
                    className="text-gray-500 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Scores Grid */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center">
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Grade</p>
                    <span 
                      className="text-3xl font-black"
                      style={{ color: GRADE_CONFIG[selectedReport.status]?.color || '#888' }}
                    >
                      {selectedReport.status || '-'}
                    </span>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center">
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Math</p>
                    <span className="text-3xl font-black text-white">{selectedReport.math_score || '-'}</span>
                  </div>
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-center">
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">ELA</p>
                    <span className="text-3xl font-black text-white">{selectedReport.ela_score || '-'}</span>
                  </div>
                </div>

                {/* Scout's Take */}
                {selectedReport.scouts_take && (
                  <div className="bg-gray-800/50 border border-orange-500/30 rounded-lg p-5 mb-6">
                    <h3 className="text-orange-500 font-bold uppercase tracking-wider text-sm mb-3">Coach's Take</h3>
                    <p className="text-gray-300 leading-relaxed">{selectedReport.scouts_take}</p>
                  </div>
                )}

                {/* Target Skills */}
                {selectedReport.target_skills?.length > 0 && (
                  <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-5">
                    <h3 className="text-white font-bold uppercase tracking-wider text-sm mb-3">Target Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedReport.target_skills.map((skill, idx) => (
                        <span 
                          key={idx}
                          className="px-3 py-1 bg-orange-500/20 text-orange-500 rounded-full text-sm font-medium"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="mt-6 text-center">
                  <Button
                    onClick={() => setSelectedReport(null)}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                  >
                    Close Report
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilmRoom;
